'use client';

import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader } from '@/components/ui/loader';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Crosshair,
  Flame,
  Search,
  Swords,
  Target,
  Users,
} from 'lucide-react';
import type { TournamentPlayerLeaderboardEntry } from '../interfaces/tournaments';
import { TournamentStatsService } from '../services/tournamentStatsService';
import { cn } from '@/lib/utils';

interface TournamentStatsTabProps {
  orgId?: string;
  tournamentId: string;
  slugOrId?: string;
  isPublic?: boolean;
}

type StatsView = 'players' | 'teams';

type SortColumn =
  | 'player'
  | 'team'
  | 'maps_played'
  | 'kda'
  | 'kd_ratio'
  | 'avg_acs'
  | 'total_damage'
  | 'avg_hs_pct';

type SortDirection = 'asc' | 'desc';

interface TeamStatsRow {
  team_id: string;
  team_name: string;
  team_tag?: string;
  player_count: number;
  total_maps: number;
  total_kills: number;
  total_deaths: number;
  total_assists: number;
  kd_ratio: number;
  avg_acs: number;
  total_damage: number;
}

function formatTeamLabel(entry: Pick<TournamentPlayerLeaderboardEntry, 'team_name' | 'team_tag'>) {
  return entry.team_tag ? `[${entry.team_tag}] ${entry.team_name}` : entry.team_name;
}

function getTopPerformer(
  stats: TournamentPlayerLeaderboardEntry[],
  key: keyof Pick<
    TournamentPlayerLeaderboardEntry,
    'avg_acs' | 'kd_ratio' | 'avg_hs_pct' | 'total_damage' | 'total_kills'
  >,
): TournamentPlayerLeaderboardEntry | null {
  if (!stats.length) return null;
  return stats.reduce((best, entry) => (entry[key] as number) > (best[key] as number) ? entry : best);
}

function aggregateTeamStats(stats: TournamentPlayerLeaderboardEntry[]): TeamStatsRow[] {
  const byTeam = new Map<string, TeamStatsRow>();

  for (const entry of stats) {
    const existing = byTeam.get(entry.team_id);
    if (!existing) {
      byTeam.set(entry.team_id, {
        team_id: entry.team_id,
        team_name: entry.team_name,
        team_tag: entry.team_tag,
        player_count: 1,
        total_maps: entry.maps_played,
        total_kills: entry.total_kills,
        total_deaths: entry.total_deaths,
        total_assists: entry.total_assists,
        kd_ratio: entry.kd_ratio,
        avg_acs: entry.avg_acs,
        total_damage: entry.total_damage,
      });
      continue;
    }

    existing.player_count += 1;
    existing.total_maps += entry.maps_played;
    existing.total_kills += entry.total_kills;
    existing.total_deaths += entry.total_deaths;
    existing.total_assists += entry.total_assists;
    existing.total_damage += entry.total_damage;
  }

  return Array.from(byTeam.values())
    .map((team) => ({
      ...team,
      kd_ratio:
        team.total_deaths > 0
          ? Number((team.total_kills / team.total_deaths).toFixed(2))
          : team.total_kills,
      avg_acs: team.player_count
        ? Math.round(
            stats
              .filter((entry) => entry.team_id === team.team_id)
              .reduce((sum, entry) => sum + entry.avg_acs, 0) / team.player_count,
          )
        : 0,
    }))
    .sort((a, b) => b.avg_acs - a.avg_acs);
}

function TopPerformerCard({
  label,
  value,
  player,
  teamLabel,
  icon: Icon,
  accentClass,
}: {
  label: string;
  value: string;
  player: string;
  teamLabel: string;
  icon: React.ElementType;
  accentClass: string;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/40 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <div className={cn('rounded-lg p-2', accentClass)}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div>
        <p className="text-2xl font-display font-bold text-foreground">{value}</p>
        <p className="text-sm font-medium text-foreground mt-1 truncate">{player}</p>
        <p className="text-xs text-muted-foreground truncate">{teamLabel}</p>
      </div>
    </div>
  );
}

function SortableHead({
  label,
  column,
  activeColumn,
  direction,
  onSort,
  className,
}: {
  label: string;
  column: SortColumn;
  activeColumn: SortColumn;
  direction: SortDirection;
  onSort: (column: SortColumn) => void;
  className?: string;
}) {
  const isActive = activeColumn === column;

  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          'inline-flex items-center gap-1 font-medium transition-colors hover:text-foreground',
          isActive ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        {label}
        {isActive ? (
          direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 opacity-50" />
        )}
      </button>
    </TableHead>
  );
}

const TournamentStatsTab: React.FC<TournamentStatsTabProps> = ({
  orgId,
  tournamentId,
  slugOrId,
  isPublic = false,
}) => {
  const [stats, setStats] = useState<TournamentPlayerLeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<StatsView>('players');
  const [searchQuery, setSearchQuery] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [sortColumn, setSortColumn] = useState<SortColumn>('avg_acs');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    const loadStats = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = isPublic && slugOrId
          ? await TournamentStatsService.getPublicPlayerStats(slugOrId)
          : orgId
            ? await TournamentStatsService.getPlayerStats(orgId, tournamentId)
            : [];
        setStats(data);
      } catch (err) {
        console.error('Error loading tournament stats:', err);
        setError('No se pudieron cargar las estadísticas del torneo');
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, [orgId, tournamentId, slugOrId, isPublic]);

  const teams = useMemo(() => {
    const unique = new Map<string, { id: string; label: string }>();
    for (const entry of stats) {
      if (!unique.has(entry.team_id)) {
        unique.set(entry.team_id, {
          id: entry.team_id,
          label: formatTeamLabel(entry),
        });
      }
    }
    return Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [stats]);

  const topPerformers = useMemo(
    () => ({
      acs: getTopPerformer(stats, 'avg_acs'),
      kd: getTopPerformer(stats, 'kd_ratio'),
      hs: getTopPerformer(stats, 'avg_hs_pct'),
      damage: getTopPerformer(stats, 'total_damage'),
    }),
    [stats],
  );

  const filteredPlayers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return stats.filter((entry) => {
      const matchesTeam = teamFilter === 'all' || entry.team_id === teamFilter;
      if (!matchesTeam) return false;
      if (!query) return true;

      const playerName = (entry.player_ign || entry.player_name).toLowerCase();
      const riotId = entry.riot_id?.toLowerCase() ?? '';
      const teamName = formatTeamLabel(entry).toLowerCase();

      return (
        playerName.includes(query) ||
        riotId.includes(query) ||
        teamName.includes(query)
      );
    });
  }, [stats, searchQuery, teamFilter]);

  const sortedPlayers = useMemo(() => {
    const sorted = [...filteredPlayers];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'player':
          comparison = (a.player_ign || a.player_name).localeCompare(b.player_ign || b.player_name);
          break;
        case 'team':
          comparison = formatTeamLabel(a).localeCompare(formatTeamLabel(b));
          break;
        case 'maps_played':
          comparison = a.maps_played - b.maps_played;
          break;
        case 'kda':
          comparison = a.total_kills - b.total_kills;
          break;
        case 'kd_ratio':
          comparison = a.kd_ratio - b.kd_ratio;
          break;
        case 'avg_acs':
          comparison = a.avg_acs - b.avg_acs;
          break;
        case 'total_damage':
          comparison = a.total_damage - b.total_damage;
          break;
        case 'avg_hs_pct':
          comparison = a.avg_hs_pct - b.avg_hs_pct;
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredPlayers, sortColumn, sortDirection]);

  const teamStats = useMemo(() => aggregateTeamStats(stats), [stats]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'player' || column === 'team' ? 'asc' : 'desc');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <p>{error}</p>
      </div>
    );
  }

  if (!stats.length) {
    return (
      <div className="text-center py-16 space-y-3">
        <BarChart3 className="w-10 h-10 mx-auto text-muted-foreground/40" />
        <p className="text-muted-foreground">
          {isPublic
            ? 'Las estadísticas se publicarán cuando haya partidas disputadas.'
            : 'Aún no hay estadísticas de jugadores en este torneo.'}
        </p>
        {!isPublic && (
          <p className="text-xs text-muted-foreground/70">
            Importa resultados con Match ID de Valorant para generar el leaderboard.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-primary" />
        <h3 className="font-display text-lg font-semibold text-foreground">Estadísticas del torneo</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {topPerformers.acs && (
          <TopPerformerCard
            label="Mayor ACS"
            value={String(topPerformers.acs.avg_acs)}
            player={topPerformers.acs.player_ign || topPerformers.acs.player_name}
            teamLabel={formatTeamLabel(topPerformers.acs)}
            icon={Target}
            accentClass="bg-primary/15 text-primary"
          />
        )}
        {topPerformers.kd && (
          <TopPerformerCard
            label="Mayor K/D"
            value={topPerformers.kd.kd_ratio.toFixed(2)}
            player={topPerformers.kd.player_ign || topPerformers.kd.player_name}
            teamLabel={formatTeamLabel(topPerformers.kd)}
            icon={Swords}
            accentClass="bg-amber-500/15 text-amber-400"
          />
        )}
        {topPerformers.hs && (
          <TopPerformerCard
            label="Mayor HS%"
            value={`${topPerformers.hs.avg_hs_pct}%`}
            player={topPerformers.hs.player_ign || topPerformers.hs.player_name}
            teamLabel={formatTeamLabel(topPerformers.hs)}
            icon={Crosshair}
            accentClass="bg-emerald-500/15 text-emerald-400"
          />
        )}
        {topPerformers.damage && (
          <TopPerformerCard
            label="Mayor daño"
            value={topPerformers.damage.total_damage.toLocaleString()}
            player={topPerformers.damage.player_ign || topPerformers.damage.player_name}
            teamLabel={formatTeamLabel(topPerformers.damage)}
            icon={Flame}
            accentClass="bg-red-500/15 text-red-400"
          />
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActiveView('players')}
          className={cn(
            'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors',
            activeView === 'players'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/40 text-muted-foreground hover:text-foreground',
          )}
        >
          <Users className="w-4 h-4" />
          Jugadores
        </button>
        <button
          type="button"
          onClick={() => setActiveView('teams')}
          className={cn(
            'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors',
            activeView === 'teams'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/40 text-muted-foreground hover:text-foreground',
          )}
        >
          <BarChart3 className="w-4 h-4" />
          Equipos
        </button>
      </div>

      {activeView === 'players' ? (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Buscar jugador o equipo..."
                className="pl-9"
              />
            </div>
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger className="w-full sm:w-56">
                <SelectValue placeholder="Filtrar por equipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los equipos</SelectItem>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-xl border border-border/60 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="w-10">#</TableHead>
                  <SortableHead
                    label="Jugador"
                    column="player"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Equipo"
                    column="team"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Mapas"
                    column="maps_played"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="text-center"
                  />
                  <SortableHead
                    label="K/D/A"
                    column="kda"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="text-center"
                  />
                  <SortableHead
                    label="K/D"
                    column="kd_ratio"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="text-center"
                  />
                  <SortableHead
                    label="ACS"
                    column="avg_acs"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="text-center"
                  />
                  <SortableHead
                    label="Daño"
                    column="total_damage"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="text-center"
                  />
                  <SortableHead
                    label="HS%"
                    column="avg_hs_pct"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="text-center"
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPlayers.length ? (
                  sortedPlayers.map((entry, index) => (
                    <TableRow key={entry.tournament_team_player_id}>
                      <TableCell className="font-medium text-muted-foreground">{index + 1}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {entry.player_ign || entry.player_name}
                          </p>
                          {entry.riot_id && (
                            <p className="text-xs text-muted-foreground">{entry.riot_id}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatTeamLabel(entry)}</TableCell>
                      <TableCell className="text-center">{entry.maps_played}</TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {entry.total_kills}/{entry.total_deaths}/{entry.total_assists}
                      </TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {entry.kd_ratio.toFixed(2)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-center font-semibold',
                          sortColumn === 'avg_acs' ? 'text-primary' : 'text-foreground',
                        )}
                      >
                        {entry.avg_acs}
                      </TableCell>
                      <TableCell className="text-center">
                        {entry.total_damage.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">{entry.avg_hs_pct}%</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-10 text-muted-foreground">
                      No hay jugadores que coincidan con los filtros.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border/60 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="w-10">#</TableHead>
                <TableHead>Equipo</TableHead>
                <TableHead className="text-center">Jugadores</TableHead>
                <TableHead className="text-center">Mapas</TableHead>
                <TableHead className="text-center">K/D/A</TableHead>
                <TableHead className="text-center">K/D</TableHead>
                <TableHead className="text-center">ACS prom.</TableHead>
                <TableHead className="text-center">Daño total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamStats.map((team, index) => (
                <TableRow key={team.team_id}>
                  <TableCell className="font-medium text-muted-foreground">{index + 1}</TableCell>
                  <TableCell className="font-medium">
                    {team.team_tag ? `[${team.team_tag}] ` : ''}{team.team_name}
                  </TableCell>
                  <TableCell className="text-center">{team.player_count}</TableCell>
                  <TableCell className="text-center">{team.total_maps}</TableCell>
                  <TableCell className="text-center font-mono text-sm">
                    {team.total_kills}/{team.total_deaths}/{team.total_assists}
                  </TableCell>
                  <TableCell className="text-center font-mono text-sm">
                    {team.kd_ratio.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-center font-semibold text-primary">
                    {team.avg_acs}
                  </TableCell>
                  <TableCell className="text-center">
                    {team.total_damage.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default TournamentStatsTab;
